import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

export const FAQ = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [openIndex, setOpenIndex] = useState(null);

  const content = {
    en: {
      title: "FREQUENTLY ASKED QUESTIONS",
      categories: [
        {
          category: "Products & Legality",
          questions: [
            {
              q: "Are these products legal?",
              a: "It depends on your country. In France, contact stun devices are Category D (legal for adults 18+). In Spain, regulations are stricter. Always check your local laws before purchasing. We have detailed information on our Legal page."
            },
            {
              q: "Do I need a license to buy a stun gun?",
              a: "In France, no license is required for Category D devices (contact stun guns). However, carrying them in public requires a legitimate reason. Projectile tasers (Category B) are heavily regulated and generally not available to the public."
            },
            {
              q: "Can I use these products anywhere?",
              a: "No. Use is only permitted in legitimate self-defense situations (necessity, immediacy, proportionality). Carrying these products in public spaces may require justification. Using them offensively is illegal."
            },
            {
              q: "Are the products effective?",
              a: "Yes, our stun guns deliver significant voltage designed to deter attackers. However, effectiveness can vary. We recommend combining multiple safety tools (alarm + stun gun + awareness) for best protection."
            }
          ]
        },
        {
          category: "Orders & Payment",
          questions: [
            {
              q: "What payment methods do you accept?",
              a: "We accept PayPal and credit/debit cards (via PayPal). All payments are secure and protected by PayPal Buyer Protection."
            },
            {
              q: "Is my payment secure?",
              a: "Yes. We use PayPal for all transactions, which provides secure encryption and buyer protection. We never see or store your card details."
            },
            {
              q: "Can I change or cancel my order?",
              a: "Contact us immediately via Instagram @fightlikeagyal. If the order hasn't shipped yet, we can modify or cancel it. Once shipped, you'll need to wait for delivery and use our return policy."
            }
          ]
        },
        {
          category: "Shipping & Delivery",
          questions: [
            {
              q: "Where do you ship?",
              a: "We ship to France, Spain, and all European Union countries. We do not currently ship to UK, Switzerland, or non-EU countries."
            },
            {
              q: "How long does delivery take?",
              a: "France & Spain: 3-5 business days. Rest of EU: 5-10 business days. Processing takes 1-2 business days before shipping."
            },
            {
              q: "How much is shipping?",
              a: "France & Spain: €4.99. Rest of EU: €7.99. FREE SHIPPING on all orders over €50!"
            },
            {
              q: "Is the packaging discrete?",
              a: "Yes! All orders are shipped in plain, unmarked packaging. There's no indication of the contents on the outside."
            },
            {
              q: "Do you provide tracking?",
              a: "Yes, all orders include tracking. You'll receive an email with tracking information once your order ships."
            }
          ]
        },
        {
          category: "Returns & Refunds",
          questions: [
            {
              q: "What is your return policy?",
              a: "You have 14 days from delivery to return products for any reason (EU consumer law). Products must be unused and in original packaging. Return shipping is at your expense."
            },
            {
              q: "How do I return a product?",
              a: "Contact us via Instagram @fightlikeagyal within 14 days of delivery. We'll provide return instructions. Once we receive the item, refund is processed within 14 days."
            },
            {
              q: "What if my product is defective?",
              a: "Contact us immediately with photos of the defect. We'll arrange a replacement or full refund including shipping costs."
            }
          ]
        },
        {
          category: "General",
          questions: [
            {
              q: "How do I contact you?",
              a: "You can reach us via email at fightlikeagyal@gmail.com or on Instagram @fightlikeagyal. We typically respond within 24-48 hours."
            },
            {
              q: "Why did you create Fight like a GYAL?",
              a: "Because women deserve to feel safe. Read our full story on the About page."
            }
          ]
        }
      ]
    },
    es: {
      title: "PREGUNTAS FRECUENTES",
      categories: [
        {
          category: "Productos y Legalidad",
          questions: [
            {
              q: "¿Son legales estos productos?",
              a: "Depende de tu país. En Francia, los dispositivos eléctricos de contacto son Categoría D (legal para mayores de 18). En España, las regulaciones son más estrictas. Siempre consulta las leyes locales antes de comprar. Tenemos información detallada en nuestra página Legal."
            },
            {
              q: "¿Necesito licencia para comprar un taser?",
              a: "En Francia, no se requiere licencia para dispositivos de Categoría D (tasers de contacto). Sin embargo, llevarlos en público requiere una razón legítima. Los tasers con proyectiles (Categoría B) están muy regulados y generalmente no están disponibles al público."
            },
            {
              q: "¿Puedo usar estos productos en cualquier lugar?",
              a: "No. El uso solo está permitido en situaciones de legítima defensa (necesidad, inmediatez, proporcionalidad). Llevar estos productos en espacios públicos puede requerir justificación. Usarlos de forma ofensiva es ilegal."
            },
            {
              q: "¿Son efectivos los productos?",
              a: "Sí, nuestros tasers entregan voltaje significativo diseñado para disuadir atacantes. Sin embargo, la efectividad puede variar. Recomendamos combinar varias herramientas de seguridad (alarma + taser + consciencia) para mejor protección."
            }
          ]
        },
        {
          category: "Pedidos y Pago",
          questions: [
            {
              q: "¿Qué métodos de pago aceptan?",
              a: "Aceptamos PayPal y tarjetas de crédito/débito (vía PayPal). Todos los pagos son seguros y están protegidos por la Protección al Comprador de PayPal."
            },
            {
              q: "¿Es seguro mi pago?",
              a: "Sí. Usamos PayPal para todas las transacciones, que proporciona encriptación segura y protección al comprador. Nunca vemos ni almacenamos los datos de tu tarjeta."
            },
            {
              q: "¿Puedo cambiar o cancelar mi pedido?",
              a: "Contáctanos inmediatamente vía Instagram @fightlikeagyal. Si el pedido aún no ha sido enviado, podemos modificarlo o cancelarlo. Una vez enviado, tendrás que esperar la entrega y usar nuestra política de devoluciones."
            }
          ]
        },
        {
          category: "Envío y Entrega",
          questions: [
            {
              q: "¿A dónde envían?",
              a: "Enviamos a Francia, España y todos los países de la Unión Europea. Actualmente no enviamos a Reino Unido, Suiza ni países fuera de la UE."
            },
            {
              q: "¿Cuánto tarda la entrega?",
              a: "Francia y España: 3-5 días laborables. Resto de UE: 5-10 días laborables. El procesamiento toma 1-2 días laborables antes del envío."
            },
            {
              q: "¿Cuánto cuesta el envío?",
              a: "Francia y España: 4,99€. Resto de UE: 7,99€. ¡ENVÍO GRATIS en todos los pedidos de más de 50€!"
            },
            {
              q: "¿El embalaje es discreto?",
              a: "¡Sí! Todos los pedidos se envían en embalaje simple, sin marcas. No hay indicación del contenido en el exterior."
            },
            {
              q: "¿Proporcionan seguimiento?",
              a: "Sí, todos los pedidos incluyen seguimiento. Recibirás un email con la información de seguimiento cuando tu pedido sea enviado."
            }
          ]
        },
        {
          category: "Devoluciones y Reembolsos",
          questions: [
            {
              q: "¿Cuál es su política de devoluciones?",
              a: "Tienes 14 días desde la entrega para devolver productos por cualquier motivo (ley de consumo UE). Los productos deben estar sin usar y en embalaje original. El envío de devolución corre por tu cuenta."
            },
            {
              q: "¿Cómo devuelvo un producto?",
              a: "Contáctanos vía Instagram @fightlikeagyal en los 14 días siguientes a la entrega. Te daremos instrucciones de devolución. Una vez recibamos el artículo, el reembolso se procesa en 14 días."
            },
            {
              q: "¿Qué pasa si mi producto está defectuoso?",
              a: "Contáctanos inmediatamente con fotos del defecto. Organizaremos un reemplazo o reembolso completo incluyendo gastos de envío."
            }
          ]
        },
        {
          category: "General",
          questions: [
            {
              q: "¿Cómo puedo contactaros?",
              a: "Puedes contactarnos por email a fightlikeagyal@gmail.com o en Instagram @fightlikeagyal. Normalmente respondemos en 24-48 horas."
            },
            {
              q: "¿Por qué creaste Fight like a GYAL?",
              a: "Porque las mujeres merecen sentirse seguras. Lee nuestra historia completa en la página About."
            }
          ]
        }
      ]
    },
    fr: {
      title: "QUESTIONS FRÉQUENTES",
      categories: [
        {
          category: "Produits et Légalité",
          questions: [
            {
              q: "Ces produits sont-ils légaux ?",
              a: "Cela dépend de votre pays. En France, les dispositifs électriques de contact sont de Catégorie D (légal pour les adultes de 18+). En Espagne, les réglementations sont plus strictes. Vérifiez toujours les lois locales avant d'acheter. Nous avons des informations détaillées sur notre page Légal."
            },
            {
              q: "Ai-je besoin d'un permis pour acheter un taser ?",
              a: "En France, aucun permis n'est requis pour les dispositifs de Catégorie D (tasers de contact). Cependant, les porter en public nécessite un motif légitime. Les tasers à projectiles (Catégorie B) sont fortement réglementés et généralement non accessibles au public."
            },
            {
              q: "Puis-je utiliser ces produits n'importe où ?",
              a: "Non. L'utilisation n'est autorisée qu'en situation de légitime défense (nécessité, immédiateté, proportionnalité). Porter ces produits dans les espaces publics peut nécessiter une justification. Les utiliser de manière offensive est illégal."
            },
            {
              q: "Les produits sont-ils efficaces ?",
              a: "Oui, nos tasers délivrent un voltage significatif conçu pour dissuader les agresseurs. Cependant, l'efficacité peut varier. Nous recommandons de combiner plusieurs outils de sécurité (alarme + taser + vigilance) pour une meilleure protection."
            }
          ]
        },
        {
          category: "Commandes et Paiement",
          questions: [
            {
              q: "Quels modes de paiement acceptez-vous ?",
              a: "Nous acceptons PayPal et les cartes de crédit/débit (via PayPal). Tous les paiements sont sécurisés et protégés par la Protection des Achats PayPal."
            },
            {
              q: "Mon paiement est-il sécurisé ?",
              a: "Oui. Nous utilisons PayPal pour toutes les transactions, qui fournit un chiffrement sécurisé et une protection acheteur. Nous ne voyons ni ne stockons jamais vos données de carte."
            },
            {
              q: "Puis-je modifier ou annuler ma commande ?",
              a: "Contactez-nous immédiatement via Instagram @fightlikeagyal. Si la commande n'a pas encore été expédiée, nous pouvons la modifier ou l'annuler. Une fois expédiée, vous devrez attendre la livraison et utiliser notre politique de retour."
            }
          ]
        },
        {
          category: "Livraison",
          questions: [
            {
              q: "Où livrez-vous ?",
              a: "Nous livrons en France, Espagne et tous les pays de l'Union Européenne. Nous ne livrons pas actuellement au Royaume-Uni, en Suisse ou dans les pays hors UE."
            },
            {
              q: "Combien de temps prend la livraison ?",
              a: "France et Espagne : 3-5 jours ouvrés. Reste de l'UE : 5-10 jours ouvrés. Le traitement prend 1-2 jours ouvrés avant l'expédition."
            },
            {
              q: "Combien coûte la livraison ?",
              a: "France et Espagne : 4,99€. Reste de l'UE : 7,99€. LIVRAISON GRATUITE pour toutes les commandes de plus de 50€ !"
            },
            {
              q: "L'emballage est-il discret ?",
              a: "Oui ! Toutes les commandes sont expédiées dans un emballage simple, sans marque. Il n'y a aucune indication du contenu à l'extérieur."
            },
            {
              q: "Fournissez-vous un suivi ?",
              a: "Oui, toutes les commandes incluent le suivi. Vous recevrez un e-mail avec les informations de suivi une fois votre commande expédiée."
            }
          ]
        },
        {
          category: "Retours et Remboursements",
          questions: [
            {
              q: "Quelle est votre politique de retour ?",
              a: "Vous avez 14 jours à compter de la livraison pour retourner les produits pour n'importe quelle raison (droit de la consommation UE). Les produits doivent être non utilisés et dans leur emballage d'origine. Les frais de retour sont à votre charge."
            },
            {
              q: "Comment retourner un produit ?",
              a: "Contactez-nous via Instagram @fightlikeagyal dans les 14 jours suivant la livraison. Nous vous fournirons les instructions de retour. Une fois l'article reçu, le remboursement est traité sous 14 jours."
            },
            {
              q: "Que faire si mon produit est défectueux ?",
              a: "Contactez-nous immédiatement avec des photos du défaut. Nous organiserons un remplacement ou un remboursement complet incluant les frais de livraison."
            }
          ]
        },
        {
          category: "Général",
          questions: [
            {
              q: "Comment vous contacter ?",
              a: "Vous pouvez nous joindre par email à fightlikeagyal@gmail.com ou sur Instagram @fightlikeagyal. Nous répondons généralement sous 24-48 heures."
            },
            {
              q: "Pourquoi avez-vous créé Fight like a GYAL ?",
              a: "Parce que les femmes méritent de se sentir en sécurité. Lisez notre histoire complète sur la page À Propos."
            }
          ]
        }
      ]
    }
  };

  const c = content[lang] || content.en;

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen pt-20 bg-[#050505]" data-testid="faq-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <HelpCircle className="w-12 h-12 text-[#FF0099] mx-auto mb-4" />
          <h1 className="font-heading text-4xl text-white font-bold">{c.title}</h1>
        </div>

        <div className="space-y-8">
          {c.categories.map((category, catIndex) => (
            <div key={catIndex}>
              <h2 className="font-heading text-xl text-[#FF0099] font-bold mb-4 border-b border-[#333333] pb-2">
                {category.category}
              </h2>
              <div className="space-y-2">
                {category.questions.map((item, qIndex) => {
                  const key = `${catIndex}-${qIndex}`;
                  const isOpen = openIndex === key;
                  return (
                    <div key={qIndex} className="bg-[#1A1A1A] border border-[#333333]">
                      <button
                        onClick={() => toggleQuestion(catIndex, qIndex)}
                        className="w-full flex items-center justify-between p-4 text-left"
                        data-testid={`faq-${catIndex}-${qIndex}`}
                      >
                        <span className="text-white font-bold text-sm pr-4">{item.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-[#FF0099] shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#A3A3A3] shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4">
                          <p className="text-[#A3A3A3] text-sm leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-[#A3A3A3] mb-4">Still have questions?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:fightlikeagyal@gmail.com"
              className="text-[#FF0099] hover:text-[#FAFF00] transition-colors font-bold"
            >
              📧 fightlikeagyal@gmail.com
            </a>
            <a 
              href="https://instagram.com/fightlikeagyal" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#FF0099] hover:text-[#FAFF00] transition-colors font-bold"
            >
              📸 @fightlikeagyal
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
